import { useState } from 'react';
import { BookmarkXFill, PlayFill } from 'react-bootstrap-icons';
import { useNavigate } from 'react-router-dom';

import { Avatar, Button, Typography } from 'antd';

import { aspectRatioPadding } from '@/components/shared/ImageRatio';
import { getCdnUrl } from '@/helpers';
import { ISavedLesson } from '@/interfaces/lesson';
import styled, { css, StyledProp } from '@/styled';

import UnmarkConfirmation from '../../UnmarkConfirmation';

interface IProps extends StyledProp {
  lesson: ISavedLesson;
}

const MoreInfo = ({ lesson, className }: IProps) => {
  const navigate = useNavigate();

  const [openUnmarkConfirmation, setOpenUnmarkConfirmation] = useState(false);

  const toggleUnmarkModal = () => setOpenUnmarkConfirmation(isOpened => !isOpened);

  const goToLesson = () => navigate(`/curso/${lesson.course_hash}/aula/${lesson.id}`);

  return (
    <>
      <UnmarkConfirmation lessonId={lesson.id} open={openUnmarkConfirmation} onCancel={toggleUnmarkModal} />

      <div className={className}>
        <div className='saved-lessons-card-actions'>
          <Button type='primary' onClick={goToLesson}>
            <Typography.Title level={5}>Acessar</Typography.Title> <PlayFill size={24} />
          </Button>

          <Button type='ghost' icon={<BookmarkXFill size={24} onClick={toggleUnmarkModal} />} />
        </div>

        <Typography.Title level={4} ellipsis onClick={goToLesson}>
          {lesson.title}
        </Typography.Title>

        <Typography.Text ellipsis>{lesson?.course_title}</Typography.Text>

        <Typography.Text className='avatar' ellipsis>
          <Avatar src={getCdnUrl(lesson?.author?.avatar)}>{lesson?.author?.name.split('')[0]}</Avatar>
          {lesson?.author?.name}
        </Typography.Text>
      </div>
    </>
  );
};

export default styled(MoreInfo)(({ theme }) => {
  return css`
    padding: ${theme.space.spacing(4)};
    background: ${theme.variables.bgElevated};
    border-bottom-right-radius: ${theme.borderRadius}px;
    border-bottom-left-radius: ${theme.borderRadius}px;
    position: absolute;
    z-index: 3;
    top: 0;
    left: 8px;
    width: calc(100% - 16px);
    opacity: 0;
    pointer-events: none;
    transition: ${theme.motionDurationMid};
    margin-top: calc(${aspectRatioPadding['16:9']} - 8px);
    border: 1px solid transparent;
    border-top: 0;

    > h4 {
      color: ${theme.colorTextBase};
      margin: 0;
      margin-bottom: ${theme.space.spacing(2)};
      cursor: pointer;
    }

    > span {
      color: ${theme.colorTextTertiary};
    }

    .saved-lessons-card-actions {
      width: 100%;
      display: flex;
      justify-content: space-between;
    }

    button {
      height: 40px;

      h5 {
        margin: 0;
        color: ${theme.variables.myCoursesCardButtonText};
      }

      margin-bottom: ${theme.space.spacing(4)};
      display: flex;
      align-items: center;
      justify-content: center;
      gap: ${theme.space.spacing(2)};
    }

    .avatar {
      margin-top: ${theme.space.spacing(2)};
      display: flex;
      align-items: center;
      gap: ${theme.space.spacing(2)};

      .ant-avatar {
        min-width: 24px;
        min-height: 24px;
        max-width: 24px;
        max-height: 24px;

        .ant-avatar-string {
          line-height: 24px;
        }
      }
    }

    &:after {
      content: '';
      width: 100%;
      height: 30px;
      position: absolute;
      top: -30px;
      left: 0;
      background: linear-gradient(180deg, transparent 20%, ${theme.colorBgBase} 100%);
    }
  `;
});
